import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { apiConfig, mealPlanApi } from '../api';
import {
  GroceryListResponse,
  GroceryListResponseCategoriesInnerItemsInner,
  MealPlanDayResponse,
  MealPlanDetailResponse,
  MealPlanSlotResponse,
  SlotInstructionsResponse,
} from '../api/generated/models';
import { LoadingSpinner } from '../components/loadingSpinner';
import { useStore } from '../context';
import { Trans, useTranslation } from '../i18n';
import { statusBadgeClass } from './patient-meal-plans';
import './patient-meal-plan-viewer.scss';

/* eslint-disable i18next/no-literal-string */

interface RecipeNutrient {
  quantity: number;
  unit: string;
}

interface RecipeData {
  recipe: {
    label: string,
    image: string,
    url: string,
    source: string,
    ingredientLines: string[],
    yield: number,
    calories: number,
    totalNutrients: Record<string, RecipeNutrient>,
  };
  link: string;
}

interface DailySummary {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  sodium_mg: number;
  cholesterol_mg: number;
  saturated_fat_g: number;
  potassium_mg: number;
  calcium_mg: number;
  iron_mg: number;
  vitamin_c_mg: number;
  vitamin_a_mcg: number;
}

const SLOT_ORDER = ['breakfast', 'lunch', 'dinner', 'snacks', 'snack'];

// SLOT_ORDER contains both "snacks" and "snack", this function normalizes slot type to "snacks" for consistency.
const normalizeSlotTypeForSwap = (slotType: string): string => {
  return slotType === 'snack' ? 'snacks' : slotType;
};

const getRecipeDataIdentity = (data: RecipeData | null | undefined): string | null => {
  if (!data) {
    return null;
  }
  // We prefer Edamam's permanent link over recipe.url over the display label, because two
  // distinct recipes can easily share a label (e.g. "Chicken Stir-Fry"). Falling all the way
  // back to label is a last resort for manually-inserted recipes that have neither URL.
  return data.link ?? data.recipe?.url ?? data.recipe?.label ?? null;
};

const findMatchingSwapSuggestionIndex = (suggestions: RecipeData[], target: RecipeData): number => {
  const targetIdentity = getRecipeDataIdentity(target);
  if (!targetIdentity) {
    return -1;
  }
  return suggestions.findIndex((s) => {
    const suggestionIdentity = getRecipeDataIdentity(s);
    return suggestionIdentity != null && suggestionIdentity === targetIdentity;
  });
};

const NUTRIENT_LABELS: Record<string, string> = {
  ENERC_KCAL: 'Calories',
  PROCNT: 'Protein',
  CHOCDF: 'Carbs',
  FAT: 'Fat',
  FIBTG: 'Fiber',
  SUGAR: 'Sugar',
  NA: 'Sodium',
  CHOLE: 'Cholesterol',
  FASAT: 'Saturated Fat',
  K: 'Potassium',
  CA: 'Calcium',
  FE: 'Iron',
  VITC: 'Vitamin C',
  VITA_RAE: 'Vitamin A',
};

// --- Macro Progress Bar ---

const useMacroBarStyles = makeStyles({
  progressBar: (props: { color: string }) => ({
    width: '100%',
    height: 20,
    borderRadius: 4,
    backgroundColor: `${props.color}20`,
    '& .MuiLinearProgress-bar': {
      backgroundColor: props.color,
    },
  }),
});

const getMacroColor = (actual: number, target: number): string => {
  if (target <= 0) {
    return '#36C2B4';
  }
  const ratio = Math.abs(actual - target) / target;
  if (ratio <= 0.1) {
    return '#36C2B4';
  }
  if (ratio <= 0.2) {
    return '#FFDC00';
  }
  return '#F15A37';
};

const MacroProgressBar = ({ label, actual, target, unit }: {
  label: string,
  actual: number,
  target: number,
  unit: string,
}) => {
  const color = getMacroColor(actual, target);
  const classes = useMacroBarStyles({ color });
  const pct = target > 0 ? Math.min((actual / target) * 100, 100) : 0;

  return (
    <div className="macroBar">
      <div className="macroBarLabel">
        <span className="macroBarName">{label}</span>
        <span className="macroBarValues">
          {target > 0 ? `${Math.round(actual)} / ${Math.round(target)} ${unit}` : `${Math.round(actual)} ${unit}`}
        </span>
      </div>
      <LinearProgress variant="determinate" value={pct} className={classes.progressBar} />
    </div>
  );
};

// --- Daily Macro Summary ---

const EXTENDED_NUTRIENTS: { key: keyof DailySummary, label: string, unit: string }[] = [
  { key: 'sugar_g', label: 'Sugar', unit: 'g' },
  { key: 'sodium_mg', label: 'Sodium', unit: 'mg' },
  { key: 'cholesterol_mg', label: 'Cholesterol', unit: 'mg' },
  { key: 'saturated_fat_g', label: 'Saturated Fat', unit: 'g' },
  { key: 'potassium_mg', label: 'Potassium', unit: 'mg' },
  { key: 'calcium_mg', label: 'Calcium', unit: 'mg' },
  { key: 'iron_mg', label: 'Iron', unit: 'mg' },
  { key: 'vitamin_c_mg', label: 'Vitamin C', unit: 'mg' },
  { key: 'vitamin_a_mcg', label: 'Vitamin A', unit: 'µg' },
];

const DailyMacroSummary = ({ day, plan }: { day: MealPlanDayResponse, plan: MealPlanDetailResponse }) => {
  const summary = day.daily_summary as DailySummary | null;
  const [showAll, setShowAll] = useState(false);
  if (!summary) {
    return null;
  }

  return (
    <div className="macroSummary">
      <div className="macroSummaryTitle">Daily Macro Summary</div>
      <MacroProgressBar label="Calories" actual={summary.calories} target={plan.target_calories ?? 0} unit="kcal" />
      <MacroProgressBar label="Protein" actual={summary.protein_g} target={plan.target_protein_g ?? 0} unit="g" />
      <MacroProgressBar label="Carbs" actual={summary.carbs_g} target={plan.target_carbs_g ?? 0} unit="g" />
      <MacroProgressBar label="Fat" actual={summary.fat_g} target={plan.target_fat_g ?? 0} unit="g" />
      <MacroProgressBar label="Fiber" actual={summary.fiber_g} target={0} unit="g" />
      {showAll && (
        <div className="extendedNutrients">
          {EXTENDED_NUTRIENTS.map(({ key, label, unit }) => (
            <div key={key} className="extendedNutrientRow">
              <span className="extendedNutrientLabel">{label}</span>
              <span className="extendedNutrientValue">
                {Math.round((summary[key] ?? 0) * 10) / 10} {unit}
              </span>
            </div>
          ))}
        </div>
      )}
      <Button
        size="small"
        className="showAllNutrientsBtn"
        onClick={() => setShowAll(!showAll)}
      >
        {showAll ? 'Hide details' : 'Show all nutrients'}
      </Button>
    </div>
  );
};

// --- AI Insight Panel ---

const AiInsightPanel = ({ text }: { text: string }) => (
  <div className="aiInsightPanel">
    <div className="insightLabel">AI Insight</div>
    <div className="insightText">{text}</div>
  </div>
);

// --- Recipe Card ---

const RecipeCard = ({ slot, onSwap, isSwapping, canEdit, onFetchInstructions, onSaveNote }: {
  slot: MealPlanSlotResponse,
  onSwap?: (slotId: number) => void,
  isSwapping?: boolean,
  canEdit?: boolean,
  onFetchInstructions?: (slotId: number) => Promise<SlotInstructionsResponse>,
  // Returns a promise so the card can await success/failure and keep the editor open + the
  // typed text intact on error (avoids silently discarding the user's note).
  onSaveNote?: (slotId: number, note: string) => Promise<void>,
}) => {
  const { t } = useTranslation();
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState<string[] | null>(null);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [instructionsError, setInstructionsError] = useState(false);
  const [instructionsSourceUrl, setInstructionsSourceUrl] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [saveNoteError, setSaveNoteError] = useState(false);
  const data = slot.recipe_data as RecipeData | null;
  const existingNote = (slot.recipe_data as any)?.clinician_notes || '';
  // Derive a stable identifier of *which recipe* this slot is currently showing. We use this
  // (rather than the slot.recipe_data reference) as the reset trigger below, because the parent
  // refetches the meal plan after every mutation — that gives slot.recipe_data a brand-new
  // object reference even when nothing about THIS slot changed, which would otherwise blow away
  // an in-progress note edit on this card whenever the user mutates a different slot.
  const currentRecipeIdentity = getRecipeDataIdentity(data);

  // Mirror of currentRecipeIdentity in a ref so async closures (e.g. the in-flight
  // onFetchInstructions request below) can compare what they captured at request-time against
  // the recipe currently mounted on the card when the response actually arrives. Without this,
  // an in-flight fetch from the OLD recipe would resolve AFTER the reset effect ran and
  // overwrite the (correctly cleared) state with stale data — so the new recipe's card would
  // end up showing the previous recipe's instructions / source URL until full page reload.
  const recipeIdentityRef = useRef(currentRecipeIdentity);
  useEffect(() => {
    recipeIdentityRef.current = currentRecipeIdentity;
  }, [currentRecipeIdentity]);

  // After a recipe swap, slot.id stays the same so React preserves this card's local state,
  // and the fetch guard `instructions == null || instructionsError` then refuses to refetch —
  // leaving the user looking at the OLD recipe's instructions/source URL under the new recipe
  // until a full page reload. Reset the cached state whenever the recipe identity changes.
  useEffect(() => {
    setShowInstructions(false);
    setInstructions(null);
    setInstructionsSourceUrl(null);
    setInstructionsError(false);
    setInstructionsLoading(false);
    setEditingNote(false);
    setNoteText('');
    setSavingNote(false);
    setSaveNoteError(false);
  }, [currentRecipeIdentity]);

  if (!data?.recipe) {
    return (
      <div className="emptySlotCard">
        <p>
          <Trans>No matching recipe found</Trans>
        </p>
        {onSwap && (
          <Button variant="outlined" size="small" onClick={() => onSwap(slot.id)}>
            <Trans>Find Recipe</Trans>
          </Button>
        )}
      </div>
    );
  }

  const { recipe, link } = data;
  const servings = Math.max(recipe.yield || 1, 1);
  const cals = recipe.totalNutrients?.ENERC_KCAL;
  const protein = recipe.totalNutrients?.PROCNT;
  const carbs = recipe.totalNutrients?.CHOCDF;
  const fat = recipe.totalNutrients?.FAT;

  const handleSwapClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSwap?.(slot.id);
  };

  return (
    <Accordion className="recipeCard" variant="outlined">
      <AccordionSummary expandIcon={<span style={{ fontSize: 20 }}>&#9660;</span>}>
        <div className="recipeSummary">
          {recipe.image
            ? <img src={recipe.image} alt={recipe.label} className="recipeImage" />
            : (
              <div className="recipePlaceholderImage">
                <i className="fas fa-utensils" />
              </div>
            )}
          <div className="recipeInfo">
            <h4 className="recipeTitle">{recipe.label}</h4>
            <div className="recipeSource">{recipe.source}</div>
            <div className="recipeChips">
              {cals && <Chip label={`${Math.round(cals.quantity / servings)} kcal`} size="small" />}
              {protein && <Chip label={`P: ${Math.round(protein.quantity / servings)}g`} size="small" />}
              {carbs && <Chip label={`C: ${Math.round(carbs.quantity / servings)}g`} size="small" />}
              {fat && <Chip label={`F: ${Math.round(fat.quantity / servings)}g`} size="small" />}
            </div>
          </div>
          {onSwap && (
            <Button
              className="swapBtn"
              variant="outlined"
              size="small"
              onClick={handleSwapClick}
              disabled={isSwapping}
            >
              {isSwapping ? <CircularProgress size={16} /> : <Trans>Swap</Trans>}
            </Button>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="recipeDetails">
          {recipe.ingredientLines && recipe.ingredientLines.length > 0 && (
            <>
              <Typography variant="subtitle2">Ingredients</Typography>
              <List dense className="ingredientsList">
                {recipe.ingredientLines.map((line, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={line} />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Clinician Note */}
          {(existingNote || canEdit) && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              {!editingNote
                ? (
                  <>
                    {existingNote && (
                      <div style={{ background: '#fff8e1', borderRadius: 4, padding: '8px 12px', marginBottom: 4 }}>
                        <Typography variant="body2" style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                          {existingNote}
                        </Typography>
                      </div>
                    )}
                    {canEdit && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setNoteText(existingNote);
                          setEditingNote(true);
                        }}
                        startIcon={<span style={{ fontSize: 14 }}>&#x270E;</span>}
                      >
                        {existingNote ? t('Edit Note') : t('Add Note')}
                      </Button>
                    )}
                  </>
                )
                : (
                  <div>
                    <TextField
                      autoFocus
                      fullWidth
                      multiline
                      rows={2}
                      rowsMax={6}
                      variant="outlined"
                      size="small"
                      placeholder={t('e.g. Replace honey with stevia, use brown rice instead of white')}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      disabled={savingNote}
                    />
                    {saveNoteError && (
                      <Typography color="error" variant="body2" style={{ marginTop: 4 }}>
                        <Trans>Failed to save note. Please try again.</Trans>
                      </Typography>
                    )}
                    <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
                      <Button size="small" onClick={() => setEditingNote(false)} disabled={savingNote}>
                        {t('Cancel')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={savingNote || !onSaveNote}
                        onClick={async () => {
                          if (!onSaveNote) {
                            return;
                          }
                          setSavingNote(true);
                          setSaveNoteError(false);
                          try {
                            await onSaveNote(slot.id, noteText);
                            setEditingNote(false);
                          } catch {
                            // Keep the editor open with the typed text intact so the user can retry.
                            setSaveNoteError(true);
                          } finally {
                            setSavingNote(false);
                          }
                        }}
                      >
                        {savingNote ? <CircularProgress size={14} /> : t('Save Note')}
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Recipe Instructions */}
          <Button
            size="small"
            style={{ marginTop: 8, marginBottom: 8 }}
            onClick={async () => {
              // While a fetch is already in flight, ignore additional clicks. Prevents the
              // user from queuing duplicate concurrent fetches by clicking rapidly, and keeps
              // the request-identity staleness guard simple (only one in-flight at a time
              // from this user-initiated path; an in-flight fetch left over from BEFORE a
              // swap is handled by the staleness check after `await` below).
              if (instructionsLoading) {
                return;
              }

              // Path A: panel is currently open → just close it. No fetch needed.
              if (showInstructions) {
                setShowInstructions(false);
                return;
              }
              // Path B: panel is closed and we already have good cached data → just open it.
              if (instructions != null && !instructionsError) {
                setShowInstructions(true);
                return;
              }
              // Path C: panel is closed and we need to fetch first.
              if (!onFetchInstructions) {
                return;
              }

              // Capture the recipe identity at request-time so we can detect if the slot's
              // recipe has been swapped out from under us by the time the response arrives.
              // Without this guard, the in-flight setInstructions/setInstructionsSourceUrl
              // calls would clobber the (correctly reset) state with stale pre-swap data.
              const requestIdentity = currentRecipeIdentity;
              setInstructionsLoading(true);
              setInstructionsError(false);
              try {
                const resData = await onFetchInstructions(slot.id);
                if (recipeIdentityRef.current !== requestIdentity) {
                  // Recipe was swapped during the fetch — drop the stale response. The reset
                  // effect already cleared the loading flag, so we don't touch it in finally.
                  return;
                }
                setInstructions(resData.instructions || []);
                setInstructionsSourceUrl(resData.source_url || null);
                setShowInstructions(true);
              } catch (err) {
                Sentry.captureException(err);
                if (recipeIdentityRef.current !== requestIdentity) {
                  return;
                }
                setInstructionsError(true);
                setInstructions(null);
                setShowInstructions(true);
              } finally {
                if (recipeIdentityRef.current === requestIdentity) {
                  setInstructionsLoading(false);
                }
              }
            }}
          >
            {instructionsLoading ? <CircularProgress size={14} style={{ marginRight: 4 }} /> : null}
            {showInstructions ? t('Hide Instructions') : t('View Instructions')}
          </Button>
          <Collapse in={showInstructions}>
            {instructionsError
              ? (
                <Typography variant="body2" color="error">
                  <Trans>Failed to load instructions. Click the button again to retry.</Trans>
                </Typography>
              )
              : instructions && instructions.length > 0
              ? (
                <ol className="instructionsList">
                  {instructions.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              )
              : instructions != null && instructions.length === 0
              ? (
                <Typography variant="body2" color="textSecondary">
                  {instructionsSourceUrl
                    ? (
                      <a href={instructionsSourceUrl} target="_blank" rel="noopener noreferrer">
                        {t('View full recipe on source site')}
                      </a>
                    )
                    : <Trans>No instructions available.</Trans>}
                </Typography>
              )
              : null}
          </Collapse>

          {(recipe.yield != null && recipe.yield > 0) && (
            <div className="recipeYield">Yield: {recipe.yield} servings</div>
          )}
          {recipe.totalNutrients && (
            <>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Nutrients</Typography>
              <div className="nutrientGrid">
                {Object.entries(NUTRIENT_LABELS).map(([key, name]) => {
                  const nutrient = recipe.totalNutrients[key];
                  if (!nutrient) {
                    return null;
                  }
                  const perServing = nutrient.quantity / servings;
                  return (
                    <div key={key} className="nutrientRow">
                      <span>{name}</span>
                      <span>{Math.round(perServing * 10) / 10} {nutrient.unit}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {(link || recipe.url) && (
            <div className="sourceLink">
              <a href={link || recipe.url} target="_blank" rel="noopener noreferrer">
                View full recipe
              </a>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const SwapSuggestionCard = (
  props: { suggestion: RecipeData, onClick: () => void, busy?: boolean, dayNumber?: number },
) => {
  const { t } = useTranslation();
  const { suggestion, onClick, busy, dayNumber } = props;
  const recipe = (suggestion as any).recipe ?? suggestion;
  const servings = Math.max(recipe.yield || 1, 1);
  const cals = recipe.totalNutrients?.ENERC_KCAL;
  const protein = recipe.totalNutrients?.PROCNT;
  const carbs = recipe.totalNutrients?.CHOCDF;
  const fat = recipe.totalNutrients?.FAT;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <div
        className={`swapSuggestionCard${busy ? ' swapSuggestionCard--disabled' : ''}`}
        onClick={onClick}
      >
        {recipe.image && (
          <img
            src={recipe.image}
            alt={recipe.label}
            style={{
              width: '100%',
              height: 120,
              objectFit: 'cover',
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
        )}
        {typeof dayNumber === 'number' && (
          <Typography variant="caption" color="textSecondary" display="block">
            {t('Day {{day}}', { day: dayNumber })}
          </Typography>
        )}
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
          {recipe.label}
        </Typography>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {cals && <Chip label={`${Math.round(cals.quantity / servings)} kcal`} size="small" />}
          {protein && <Chip label={`P: ${Math.round(protein.quantity / servings)}g`} size="small" />}
          {carbs && <Chip label={`C: ${Math.round(carbs.quantity / servings)}g`} size="small" />}
          {fat && <Chip label={`F: ${Math.round(fat.quantity / servings)}g`} size="small" />}
        </div>
      </div>
    </Grid>
  );
};

// --- Main Viewer ---

export const MealPlanViewer = () => {
  const { patient } = useStore();
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const queryClient = useQueryClient();
  const { mealPlanId } = useParams<{ mealPlanId: string }>();

  const [activeDay, setActiveDay] = useState(0);
  const [generationTriggered, setGenerationTriggered] = useState(false);
  const [swapDialogSlotId, setSwapDialogSlotId] = useState<number | null>(null);
  const [swapKeywords, setSwapKeywords] = useState('');
  const [swapSuggestions, setSwapSuggestions] = useState<RecipeData[] | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [groceryDialogOpen, setGroceryDialogOpen] = useState(false);
  const [groceryData, setGroceryData] = useState<GroceryListResponse | null>(null);
  const [checkedGroceryItems, setCheckedGroceryItems] = useState<Set<string>>(new Set());

  const basePath = match.url.replace(/\/[^/]+$/, '');
  const planId = Number(mealPlanId);

  const query = useQuery(
    ['meal-plan', patient?.patient_id, planId],
    async () => {
      const res = await mealPlanApi.appApiMealPlanGetMealPlan({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
      });
      return res.data;
    },
    {
      enabled: !!patient?.patient_id && !isNaN(planId),
      refetchInterval: (data) => data?.status === 'generating' ? 3000 : false,
    },
  );

  const plan = query.data;

  const generateMutation = useMutation(
    async () => {
      const res = await mealPlanApi.appApiMealPlanPostGenerateMealPlan({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meal-plan', patient.patient_id, planId]);
        queryClient.invalidateQueries(['meal-plans', patient.patient_id]);
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const finalizeMutation = useMutation(
    async () => {
      const res = await mealPlanApi.appApiMealPlanPostFinalizeMealPlan({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meal-plan', patient.patient_id, planId]);
        queryClient.invalidateQueries(['meal-plans', patient.patient_id]);
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const swapSuggestionsMutation = useMutation(
    async ({ slotId, keywords }: { slotId: number, keywords: string }) => {
      const res = await mealPlanApi.appApiMealPlanPostSwapSuggestions({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
        SwapSuggestionsRequest: { slot_id: slotId, keywords },
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        setSwapSuggestions((data.suggestions ?? []) as RecipeData[]);
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const swapConfirmMutation = useMutation(
    async ({ slotId, suggestionIndex, expectedRecipeLabel }: {
      slotId: number,
      suggestionIndex: number,
      expectedRecipeLabel?: string,
    }) => {
      const res = await mealPlanApi.appApiMealPlanPostSwapConfirm({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
        SwapConfirmRequest: {
          slot_id: slotId,
          suggestion_index: suggestionIndex,
          expected_recipe_label: expectedRecipeLabel,
        },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meal-plan', patient.patient_id, planId]);
        setSwapDialogSlotId(null);
        setSwapKeywords('');
        setSwapSuggestions(null);
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const NO_MATCH_ERROR = 'swap suggestions do not contain the target recipe';
  const swapRecipeFromMealPlanMutation = useMutation(
    async (targetSlot: MealPlanSlotResponse) => {
      if (swapDialogSlotId == null) {
        throw new Error('swap dialog slot id is null');
      }

      const targetData = targetSlot.recipe_data as RecipeData | null;
      if (!targetData?.recipe?.label?.trim()) {
        throw new Error('target recipe has no label');
      }

      const keywords = targetData.recipe.label.trim();
      const postSwapSuggestionsRes = await mealPlanApi.appApiMealPlanPostSwapSuggestions({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
        SwapSuggestionsRequest: { slot_id: swapDialogSlotId, keywords },
      });
      const swapSuggestions = (postSwapSuggestionsRes.data.suggestions ?? []) as RecipeData[];

      const idx = findMatchingSwapSuggestionIndex(swapSuggestions, targetData);
      if (idx < 0) {
        throw new Error(NO_MATCH_ERROR);
      }

      const postSwapConfirmRes = await mealPlanApi.appApiMealPlanPostSwapConfirm({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
        SwapConfirmRequest: {
          slot_id: swapDialogSlotId,
          suggestion_index: idx,
          expected_recipe_label: targetData.recipe.label,
        },
      });
      return postSwapConfirmRes.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meal-plan', patient.patient_id, planId]);
        setSwapDialogSlotId(null);
        setSwapKeywords('');
        setSwapSuggestions(null);
      },
      onError: (err) => {
        if (err instanceof Error && err.message === NO_MATCH_ERROR) {
          return;
        }
        Sentry.captureException(err);
      },
    },
  );

  const groceryListMutation = useMutation(
    async () => {
      const res = await mealPlanApi.appApiMealPlanPostGroceryList({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        setGroceryData(data);
        setCheckedGroceryItems(new Set());
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const saveNoteMutation = useMutation(
    async ({ slotId, note }: { slotId: number, note: string }) => {
      const res = await mealPlanApi.appApiMealPlanPostSlotNote({
        patient_id: patient.patient_id,
        meal_plan_id: planId,
        slot_id: slotId,
        AppApiMealPlanPostSlotNoteRequest: { note },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meal-plan', patient.patient_id, planId]);
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    },
  );

  const handleOpenGroceryList = () => {
    // Clear any cached data from a previous open before kicking off the new mutation.
    // Otherwise, if the new regeneration fails, the dialog would still render and let
    // the clinician copy a stale grocery list (e.g. one captured before swapping recipes).
    setGroceryData(null);
    setCheckedGroceryItems(new Set());
    groceryListMutation.reset();
    setGroceryDialogOpen(true);
    groceryListMutation.mutate();
  };

  const handleToggleGroceryItem = (itemKey: string) => {
    setCheckedGroceryItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const handleToggleCategory = (
    categoryName: string,
    items: GroceryListResponseCategoriesInnerItemsInner[],
  ) => {
    setCheckedGroceryItems((prev) => {
      const next = new Set(prev);
      const keys = items.map((item) => `${categoryName}:${item.name}`);
      const allChecked = keys.every((k) => next.has(k));
      keys.forEach((k) => allChecked ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const handleCopyGroceryList = () => {
    if (!groceryData?.categories) {
      return;
    }
    const lines: string[] = [];
    for (const cat of groceryData.categories) {
      lines.push(`\n${cat.name ?? ''}`);
      for (const item of cat.items ?? []) {
        const checked = checkedGroceryItems.has(`${cat.name}:${item.name}`);
        lines.push(`  ${checked ? '[x]' : '[ ]'} ${item.name}${item.quantity ? ` — ${item.quantity}` : ''}`);
      }
    }
    navigator.clipboard.writeText(lines.join('\n').trim());
  };

  const handleDownloadPdf = useCallback(async () => {
    setPdfDownloading(true);
    setPdfError(null);
    try {
      const token = typeof apiConfig.accessToken === 'function'
        ? await apiConfig.accessToken()
        : apiConfig.accessToken;
      const url = `${apiConfig.basePath}/patients/${patient.patient_id}/meal-plans/${planId}/pdf`;
      const res = await axios.post(url, null, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const contentType = res.headers['content-type'] || 'application/pdf';
      const isHtml = contentType.includes('text/html');
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = isHtml ? `meal-plan-${planId}.html` : `meal-plan-${planId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      const message = err?.response?.status
        ? `PDF download failed (${err.response.status})`
        : err?.message || 'PDF download failed';
      setPdfError(message);
      Sentry.captureException(err);
    } finally {
      setPdfDownloading(false);
    }
  }, [patient?.patient_id, planId]);

  // Auto-trigger generation for draft plans. Dependencies are intentionally
  // limited to plan?.status — the guard flags (generationTriggered, isLoading,
  // isError) are only read to prevent re-firing, not to trigger the effect.
  useEffect(() => {
    if (
      plan && plan.status === 'draft' && !generationTriggered && !generateMutation.isLoading
      && !generateMutation.isError
    ) {
      setGenerationTriggered(true);
      generateMutation.mutate();
    }
  }, [plan?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedDays = useMemo(() => {
    if (!plan?.days) {
      return [];
    }
    return [...plan.days].sort((a, b) => a.day_number - b.day_number);
  }, [plan?.days]);

  useEffect(() => {
    if (activeDay >= sortedDays.length && sortedDays.length > 0) {
      setActiveDay(0);
    }
  }, [sortedDays.length, activeDay]);

  const currentDay = sortedDays[activeDay];

  const groupedSlots = useMemo(() => {
    if (!currentDay?.slots) {
      return {};
    }
    const groups: Record<string, MealPlanSlotResponse[]> = {};
    for (const slot of currentDay.slots) {
      const type = slot.slot_type || 'other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(slot);
    }
    // Sort within each group by sort_order
    for (const type of Object.keys(groups)) {
      groups[type].sort((a, b) => a.sort_order - b.sort_order);
    }
    return groups;
  }, [currentDay]);

  const swapDialogSlot = useMemo(() => {
    if (swapDialogSlotId == null || sortedDays.length === 0) {
      return null;
    }
    for (const day of sortedDays) {
      for (const slot of day.slots || []) {
        if (slot.id === swapDialogSlotId) {
          return { dayNumber: day.day_number, slot };
        }
      }
    }
    return null;
  }, [swapDialogSlotId, sortedDays]);

  const swapOptionsFromMealPlan = useMemo(() => {
    if (swapDialogSlotId == null || !swapDialogSlot) {
      return [];
    }
    const targetNorm = normalizeSlotTypeForSwap(swapDialogSlot.slot.slot_type);
    const rows: { dayNumber: number, slot: MealPlanSlotResponse }[] = [];
    for (const day of sortedDays) {
      for (const slot of day.slots || []) {
        if (slot.id === swapDialogSlotId) {
          continue;
        }
        if (normalizeSlotTypeForSwap(slot.slot_type) !== targetNorm) {
          continue;
        }
        const rd = slot.recipe_data as RecipeData | null;
        if (!rd?.recipe) {
          continue;
        }
        rows.push({ dayNumber: day.day_number, slot });
      }
    }
    return rows;
  }, [swapDialogSlotId, swapDialogSlot, sortedDays]);

  const handleBack = () => {
    history.push(basePath);
  };

  const handleRetryGeneration = () => {
    setGenerationTriggered(true);
    generateMutation.mutate();
  };

  const handleSwapOpen = (slotId: number) => {
    setSwapDialogSlotId(slotId);
    setSwapKeywords('');
    setSwapSuggestions(null);
    swapSuggestionsMutation.reset();
    swapConfirmMutation.reset();
    swapRecipeFromMealPlanMutation.reset();
  };

  const handleSwapSearch = () => {
    if (swapDialogSlotId == null || !swapKeywords.trim()) {
      return;
    }
    swapRecipeFromMealPlanMutation.reset();
    setSwapSuggestions(null);
    swapSuggestionsMutation.mutate({ slotId: swapDialogSlotId, keywords: swapKeywords.trim() });
  };

  const handleSwapSelect = (suggestion: RecipeData, suggestionIndex: number) => {
    if (swapDialogSlotId == null) {
      return;
    }
    // Guard against double-clicks / multiple concurrent confirms — last-write-wins races
    // would otherwise let a second click override the first selection on slow connections.
    if (swapConfirmMutation.isLoading || swapRecipeFromMealPlanMutation.isLoading) {
      return;
    }
    // Pass the recipe label so the server can detect a stale-suggestions race
    // (e.g. another tab issued swap-suggestions in parallel and overwrote the cache).
    const expectedRecipeLabel = (suggestion as any)?.recipe?.label ?? (suggestion as any)?.label ?? undefined;
    swapConfirmMutation.mutate({
      slotId: swapDialogSlotId,
      suggestionIndex,
      expectedRecipeLabel,
    });
  };

  const handleSwapBack = () => {
    setSwapSuggestions(null);
    swapConfirmMutation.reset();
    swapRecipeFromMealPlanMutation.reset();
  };

  const swapActionBusy = swapConfirmMutation.isLoading || swapRecipeFromMealPlanMutation.isLoading;

  const isGenerating = generateMutation.isLoading || plan?.status === 'generating';
  const isGenerated = plan?.status === 'generated' || plan?.status === 'finalized';
  const isFinalized = plan?.status === 'finalized';

  if (query.isLoading) {
    return (
      <Grid container style={{ marginTop: 30, marginBottom: 30 }}>
        <Grid item md={10} lg={8}>
          <LoadingSpinner />
        </Grid>
      </Grid>
    );
  }

  if (query.isError) {
    return (
      <Grid container style={{ marginTop: 30, marginBottom: 30 }}>
        <Grid item md={10} lg={8}>
          <div className="mealPlanViewer">
            <Button onClick={handleBack} className="backBtn">
              &lt; <Trans>Back to Meal Plans</Trans>
            </Button>
            <Typography color="error">
              <Trans>Failed to load meal plan.</Trans>
            </Typography>
            <Button variant="outlined" onClick={() => query.refetch()} style={{ marginTop: 8 }}>
              <Trans>Retry</Trans>
            </Button>
          </div>
        </Grid>
      </Grid>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div>
          <Trans>Error loading meal plan viewer</Trans>
        </div>
      )}
      onError={(error) => Sentry.captureException(error)}
    >
      <Grid container style={{ marginTop: 30, marginBottom: 30 }}>
        <Grid item md={10} lg={8}>
          <div className="mealPlanViewer">
            {/* Header */}
            <div className="viewerHeader">
              <Button onClick={handleBack} className="backBtn">
                &lt; <Trans>Back to Meal Plans</Trans>
              </Button>
              <div className="titleRow">
                <h2>{plan.title}</h2>
                <span className={statusBadgeClass(plan.status)}>{t(plan.status)}</span>
                <div className="headerActions">
                  {isGenerated && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleOpenGroceryList}
                      disabled={groceryListMutation.isLoading}
                    >
                      {groceryListMutation.isLoading ? <CircularProgress size={16} /> : <Trans>Grocery List</Trans>}
                    </Button>
                  )}
                  {isGenerated && !isFinalized && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => finalizeMutation.mutate()}
                      disabled={finalizeMutation.isLoading}
                    >
                      {finalizeMutation.isLoading ? <CircularProgress size={16} /> : <Trans>Finalize</Trans>}
                    </Button>
                  )}
                  {isFinalized && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDownloadPdf}
                        disabled={pdfDownloading}
                      >
                        {pdfDownloading ? <CircularProgress size={16} /> : <Trans>Download PDF</Trans>}
                      </Button>
                      {pdfError && (
                        <Typography color="error" variant="caption" style={{ alignSelf: 'center' }}>
                          {pdfError}
                        </Typography>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Generation error */}
            {generateMutation.isError && (
              <div className="errorBanner">
                <Typography color="error" variant="body2">
                  <Trans>Failed to generate meal plan. Please try again.</Trans>
                </Typography>
                <Button variant="outlined" size="small" onClick={handleRetryGeneration}>
                  <Trans>Retry Generation</Trans>
                </Button>
              </div>
            )}

            {/* AI Insight */}
            {plan.ai_insight && <AiInsightPanel text={plan.ai_insight} />}

            {/* Day Tabs + Content */}
            {sortedDays.length > 0 && (
              <>
                {/* Macro Summary for active day */}
                {currentDay && <DailyMacroSummary day={currentDay} plan={plan} />}

                <Tabs
                  value={activeDay}
                  onChange={(_, val) => setActiveDay(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                  className="dayTabs"
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {sortedDays.map((day) => <Tab key={day.id} label={`Day ${day.day_number}`} />)}
                </Tabs>

                {/* Slot Groups */}
                {currentDay
                  && [...SLOT_ORDER, ...Object.keys(groupedSlots).filter((t) => !SLOT_ORDER.includes(t))].map(
                    (slotType) => {
                      const slots = groupedSlots[slotType];
                      if (!slots || slots.length === 0) {
                        return null;
                      }
                      return (
                        <div key={slotType} className="slotGroup">
                          <div className="slotGroupTitle">{slotType}</div>
                          {slots.map((slot) => (
                            <RecipeCard
                              key={slot.id}
                              slot={slot}
                              onSwap={isGenerated && !isFinalized ? handleSwapOpen : undefined}
                              isSwapping={swapActionBusy && swapDialogSlotId === slot.id}
                              canEdit={isGenerated && !isFinalized}
                              onSaveNote={isGenerated && !isFinalized
                                // Use mutateAsync so the card can await success/failure and show
                                // an inline error if the save request fails (preserves the user's
                                // typed text instead of silently discarding it).
                                ? async (slotId, note) => {
                                  await saveNoteMutation.mutateAsync({ slotId, note });
                                }
                                : undefined}
                              onFetchInstructions={async (slotId) => {
                                const res = await mealPlanApi.appApiMealPlanGetSlotInstructions({
                                  patient_id: patient.patient_id,
                                  meal_plan_id: planId,
                                  slot_id: slotId,
                                });
                                return res.data;
                              }}
                            />
                          ))}
                        </div>
                      );
                    },
                  )}
              </>
            )}

            {/* Generating state — inline spinner */}
            {isGenerating && sortedDays.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <CircularProgress size={32} />
                <Typography color="textSecondary" style={{ marginTop: 12 }}>
                  <Trans>Generating meal plan — this may take a minute…</Trans>
                </Typography>
              </div>
            )}

            {/* Empty state for plans without days */}
            {!isGenerating && plan.status !== 'draft' && sortedDays.length === 0 && (
              <Typography color="textSecondary" style={{ textAlign: 'center', padding: 40 }}>
                <Trans>No meal plan data available.</Trans>
              </Typography>
            )}

            {/* Finalize error */}
            {finalizeMutation.isError && (
              <div className="errorBanner">
                <Typography color="error" variant="body2">
                  <Trans>Failed to finalize meal plan.</Trans>
                </Typography>
              </div>
            )}
          </div>
        </Grid>
      </Grid>

      {/* Grocery List Dialog */}
      <Dialog open={groceryDialogOpen} onClose={() => setGroceryDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Trans>Grocery List</Trans>
        </DialogTitle>
        <DialogContent>
          {groceryListMutation.isLoading && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                <Trans>Generating grocery list...</Trans>
              </Typography>
            </div>
          )}
          {groceryListMutation.isError && (
            <Typography color="error" variant="body2">
              <Trans>Failed to generate grocery list.</Trans>
            </Typography>
          )}
          {groceryData?.categories && groceryData.categories.map((category) => {
            const categoryItems = category.items ?? [];
            const categoryKeys = categoryItems.map((item) => `${category.name}:${item.name}`);
            const allChecked = categoryKeys.length > 0 && categoryKeys.every((k) => checkedGroceryItems.has(k));
            return (
              <div key={category.name} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={allChecked}
                    indeterminate={!allChecked && categoryKeys.some((k) => checkedGroceryItems.has(k))}
                    onChange={() => handleToggleCategory(category.name ?? '', categoryItems)}
                    size="small"
                  />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {category.name}
                  </Typography>
                </div>
                <List dense style={{ paddingLeft: 16 }}>
                  {categoryItems.map((item, idx) => {
                    const itemKey = `${category.name}:${item.name}`;
                    return (
                      <ListItem key={idx} dense button onClick={() => handleToggleGroceryItem(itemKey)}>
                        <Checkbox
                          checked={checkedGroceryItems.has(itemKey)}
                          size="small"
                          style={{ marginRight: 8 }}
                          // Stop propagation so the click doesn't also fire the parent ListItem's
                          // onClick (which would toggle a second time and cancel out).
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleGroceryItem(itemKey)}
                        />
                        <ListItemText
                          primary={item.name}
                          secondary={item.quantity || undefined}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          {groceryData?.categories && (
            <Button onClick={handleCopyGroceryList} size="small">
              <Trans>Copy to Clipboard</Trans>
            </Button>
          )}
          <Button onClick={() => setGroceryDialogOpen(false)}>
            <Trans>Close</Trans>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Swap Recipe Dialog */}
      <Dialog
        open={swapDialogSlotId != null}
        onClose={() => {
          if (swapActionBusy) {
            return;
          }
          setSwapDialogSlotId(null);
          setSwapKeywords('');
          setSwapSuggestions(null);
          swapSuggestionsMutation.reset();
          swapConfirmMutation.reset();
          swapRecipeFromMealPlanMutation.reset();
        }}
        fullWidth
        maxWidth={swapSuggestions || swapOptionsFromMealPlan.length > 0 ? 'md' : 'sm'}
      >
        <DialogTitle>
          <Trans>Swap Recipe</Trans>
        </DialogTitle>
        <DialogContent>
          {!swapSuggestions
            ? (
              <>
                <Typography variant="body2" style={{ marginBottom: 12 }}>
                  <Trans>Enter keywords to search for a replacement recipe.</Trans>
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t('e.g. grilled chicken, vegetable soup')}
                  value={swapKeywords}
                  onChange={(e) => setSwapKeywords(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSwapSearch();
                    }
                  }}
                  disabled={swapSuggestionsMutation.isLoading || swapActionBusy}
                />
                {swapSuggestionsMutation.isError && (
                  <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                    <Trans>Failed to search recipes. Please try again.</Trans>
                  </Typography>
                )}
                {swapOptionsFromMealPlan.length > 0 && swapDialogSlot && (
                  <div style={{ marginTop: 20 }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      {t('Other {{mealType}} recipes in this plan', {
                        mealType: swapDialogSlot.slot.slot_type,
                      })}
                    </Typography>
                    <Grid container spacing={2}>
                      {swapOptionsFromMealPlan.map(({ dayNumber, slot: sourceSlot }) => {
                        const rd = sourceSlot.recipe_data as RecipeData;
                        const busy = swapRecipeFromMealPlanMutation.isLoading;
                        return (
                          <SwapSuggestionCard
                            key={sourceSlot.id}
                            suggestion={rd}
                            onClick={() => {
                              if (!busy) {
                                swapRecipeFromMealPlanMutation.mutate(sourceSlot);
                              }
                            }}
                            busy={busy}
                            dayNumber={dayNumber}
                          />
                        );
                      })}
                    </Grid>
                  </div>
                )}
                {swapRecipeFromMealPlanMutation.isError && (
                  <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
                    <Trans>Failed to swap meal from plan. Try keyword search.</Trans>
                  </Typography>
                )}
                {swapRecipeFromMealPlanMutation.isLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="textSecondary">
                      <Trans>Applying meal…</Trans>
                    </Typography>
                  </div>
                )}
              </>
            )
            : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <Button size="small" onClick={handleSwapBack} disabled={swapActionBusy}>
                    &lt; <Trans>Back</Trans>
                  </Button>
                  <Typography variant="body2" style={{ marginLeft: 8 }}>
                    {t('{{count}} results for "{{keywords}}"', {
                      count: swapSuggestions.length,
                      keywords: swapKeywords,
                    })}
                  </Typography>
                </div>
                {swapSuggestions.length === 0
                  ? (
                    <Typography color="textSecondary" style={{ textAlign: 'center', padding: 20 }}>
                      <Trans>No recipes found. Try different keywords.</Trans>
                    </Typography>
                  )
                  : (
                    <Grid container spacing={2}>
                      {swapSuggestions.map((suggestion, idx) => {
                        return (
                          <SwapSuggestionCard
                            key={idx}
                            suggestion={suggestion}
                            onClick={() => handleSwapSelect(suggestion, idx)}
                            busy={swapActionBusy}
                          />
                        );
                      })}
                    </Grid>
                  )}
                {swapConfirmMutation.isError && (
                  <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                    <Trans>Failed to swap recipe. Please try again.</Trans>
                  </Typography>
                )}
              </>
            )}
        </DialogContent>
        <DialogActions>
          {!swapSuggestions
            ? (
              <>
                <Button
                  onClick={() => setSwapDialogSlotId(null)}
                  disabled={swapSuggestionsMutation.isLoading || swapActionBusy}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  onClick={handleSwapSearch}
                  color="primary"
                  variant="contained"
                  disabled={!swapKeywords.trim() || swapSuggestionsMutation.isLoading || swapActionBusy}
                >
                  {swapSuggestionsMutation.isLoading ? <CircularProgress size={16} /> : <Trans>Search</Trans>}
                </Button>
              </>
            )
            : (
              <Button onClick={() => setSwapDialogSlotId(null)} disabled={swapActionBusy}>
                <Trans>Cancel</Trans>
              </Button>
            )}
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  );
};

/* eslint-enable i18next/no-literal-string */
